import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users,
  FileText,
  CreditCard,
  TrendingUp,
  Plus,
  ArrowRight,
  Clock,
  CheckCircle,
  AlertCircle,
  PoundSterling,
  UserCheck,
  Shield,
  Activity
} from 'lucide-react';
import api from '../../utils/api';

const DASHBOARD_CACHE_KEY = 'staffDashboardCache_v1';

const readDashboardCache = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(DASHBOARD_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.warn('Failed to read staff dashboard cache:', error);
    return null;
  }
};

const writeDashboardCache = (data) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(
      DASHBOARD_CACHE_KEY,
      JSON.stringify({ ...data, timestamp: Date.now() })
    );
  } catch (error) {
    console.warn('Failed to write staff dashboard cache:', error);
  }
};

const MobileStaffDashboard = () => {
  const cacheRef = useRef(null);
  const hasFetchedRef = useRef(false);

  const getCachedSnapshot = () => {
    if (cacheRef.current !== null) {
      return cacheRef.current;
    }
    const snapshot = readDashboardCache();
    cacheRef.current = snapshot;
    return snapshot;
  };

  const cachedSnapshot = getCachedSnapshot();

  const [stats, setStats] = useState(cachedSnapshot?.stats || {
    totalClients: 0,
    totalInvoices: 0,
    totalRevenue: 0,
    pendingInvoices: 0
  });
  const [subscriptionStats, setSubscriptionStats] = useState(cachedSnapshot?.subscriptionStats || {
    totalActiveSubscriptions: 0,
    paidSubscriptions: 0,
    trialSubscriptions: 0,
    cancelledSubscriptions: 0,
    newSubscriptionsThisMonth: 0,
    currentMonthRevenue: 0
  });
  const [recentInvoices, setRecentInvoices] = useState(cachedSnapshot?.recentInvoices || []);
  const [recentClients, setRecentClients] = useState(cachedSnapshot?.recentClients || []);
  const [recentActivity, setRecentActivity] = useState(cachedSnapshot?.activityLogs || []);
  const [monthlyRevenue, setMonthlyRevenue] = useState(cachedSnapshot?.monthlyRevenue || []);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

useEffect(() => {
  if (!hasFetchedRef.current) {
    return;
  }
  writeDashboardCache({
    stats,
    subscriptionStats,
    recentInvoices,
    recentClients,
    activityLogs: recentActivity,
    monthlyRevenue
  });
}, [stats, subscriptionStats, recentInvoices, recentClients, recentActivity, monthlyRevenue]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch CRITICAL data in parallel (FAST - DB queries only)
      const [statsResponse, invoicesResponse, clientsResponse] = await Promise.all([
        api.get('/api/dashboard/staff/stats'),
        api.get('/api/dashboard/staff/invoices/recent?limit=5'),
        api.get('/api/dashboard/staff/clients/recent?limit=5')
      ]);
      
      // Fetch OPTIONAL data in background (don't wait for these - they're slow)
      // These will update the UI when they complete (if they complete)
      Promise.all([
        api.get('/api/dashboard/staff/subscription-stats').catch(err => {
          console.warn('Subscription stats failed (non-critical):', err.message);
          return null;
        }),
        api.get('/api/dashboard/staff/activity-logs?limit=10').catch(err => {
          console.warn('Activity logs failed (non-critical):', err.message);
          return null;
        }),
        api.get('/api/dashboard/staff/monthly-revenue').catch(err => {
          console.warn('Monthly revenue failed (non-critical):', err.message);
          return null;
        })
      ]).then(([subscriptionStatsResponse, activityLogsResponse, monthlyRevenueResponse]) => {
        // Update subscription stats if available
        if (subscriptionStatsResponse && subscriptionStatsResponse.data.success && subscriptionStatsResponse.data.data) {
          const subData = subscriptionStatsResponse.data.data;
          setSubscriptionStats({
            totalActiveSubscriptions: Number(subData.totalActiveSubscriptions) || 0,
            paidSubscriptions: Number(subData.paidSubscriptions) || 0,
            trialSubscriptions: Number(subData.trialSubscriptions) || 0,
            cancelledSubscriptions: Number(subData.cancelledSubscriptions) || 0,
            newSubscriptionsThisMonth: Number(subData.newSubscriptionsThisMonth) || 0,
            currentMonthRevenue: Number(subData.currentMonthRevenue) || 0
          });
        }
        
        // Update activity logs if available
        if (activityLogsResponse && activityLogsResponse.data.success) {
          setRecentActivity(activityLogsResponse.data.data || []);
        }
        
        // Update monthly revenue if available
        if (monthlyRevenueResponse && monthlyRevenueResponse.data.success) {
          const revenueData = monthlyRevenueResponse.data.data;
          if (Array.isArray(revenueData)) {
            setMonthlyRevenue(revenueData);
          } else if (revenueData && typeof revenueData === 'object') {
            const months = Object.keys(revenueData).filter(k => !['totalRevenue', 'averageRevenue', 'growthRate'].includes(k));
            const revenueArray = months.map(month => ({
              month,
              revenue: revenueData[month] || 0
            }));
            setMonthlyRevenue(revenueArray);
          }
        }
      });
      
      // Process stats
      if (statsResponse.data.success && statsResponse.data.data) {
        const statsData = statsResponse.data.data;
        console.log('📊 MOBILE DASHBOARD - Full Stats Data:', statsData);
        console.log('📊 All fields in response:', Object.keys(statsData));
        
        // Try multiple field variations for revenue
        const revenue = statsData.totalRevenue || 
                       statsData.total_revenue || 
                       statsData.monthlyRecurringRevenue || 
                       statsData.monthly_recurring_revenue || 
                       0;
        
        // Try multiple field variations for pending
        const pending = statsData.pendingApproval || 
                       statsData.pending_approval || 
                       statsData.pendingInvoices || 
                       statsData.pending_invoices || 
                       0;
        
        console.log('📊 Revenue found:', revenue);
        console.log('📊 Pending found:', pending);
        
        setStats({
          totalClients: Number(statsData.totalUsers) || 0,
          totalInvoices: Number(statsData.totalInvoices) || 0,
          totalRevenue: Number(revenue),
          pendingInvoices: Number(pending)
        });
      }
      
      // Process recent invoices immediately (from parallel fetch)
      if (invoicesResponse.data.success) {
        setRecentInvoices(invoicesResponse.data.data || []);
      }
      
      // Process recent clients immediately (from parallel fetch)
      if (clientsResponse.data.success) {
        setRecentClients(clientsResponse.data.data || []);
      }
      
      hasFetchedRef.current = true;

      // Note: Subscription stats, activity logs, and monthly revenue are processed
      // in the background Promise.all.then() callback above (lines 72-105)
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const QuickActionCard = ({ icon: Icon, title, description, href, color = 'blue' }) => (
    <Link to={href} className="block">
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={`bg-gradient-to-br from-${color}-50 to-${color}-100 p-4 rounded-xl border border-${color}-200`}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className={`w-10 h-10 bg-${color}-500 rounded-lg flex items-center justify-center mb-3`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
            <p className="text-sm text-gray-600">{description}</p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </div>
      </motion.div>
    </Link>
  );

  const StatCard = ({ title, value, icon: Icon, color = 'blue', change }) => (
    <div className={`bg-gradient-to-br from-${color}-50 to-${color}-100 p-4 rounded-xl border border-${color}-200`}>
      <div className="flex items-center justify-between mb-2">
        <div className={`w-8 h-8 bg-${color}-500 rounded-lg flex items-center justify-center`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        {change && (
          <span className={`text-xs font-medium ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {change > 0 ? '+' : ''}{change}%
          </span>
        )}
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-1">{value}</h3>
      <p className="text-sm text-gray-600">{title}</p>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-32 bg-gray-200 rounded-xl animate-pulse"></div>
        <div className="grid grid-cols-2 gap-4">
          <div className="h-24 bg-gray-200 rounded-xl animate-pulse"></div>
          <div className="h-24 bg-gray-200 rounded-xl animate-pulse"></div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-200 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-4"
      >
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Staff Dashboard</h1>
        <p className="text-gray-600">System-wide overview and management</p>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="space-y-3"
      >
        <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
        <div className="grid grid-cols-1 gap-3">
          <QuickActionCard
            icon={Users}
            title="Client Management"
            description="Manage client accounts and relationships"
            href="/staff/manage-clients"
            color="blue"
          />
          <QuickActionCard
            icon={FileText}
            title="Invoice Oversight"
            description="Monitor and manage all invoices"
            href="/staff/invoices"
            color="green"
          />
          <QuickActionCard
            icon={UserCheck}
            title="Staff Management"
            description="Manage staff accounts and permissions"
            href="/staff/staff"
            color="purple"
          />
        </div>
      </motion.div>

      {/* Stats Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-2 gap-4"
      >
        <StatCard
          title="Total Clients"
          value={stats.totalClients || 0}
          icon={Users}
          color="blue"
        />
        <StatCard
          title="Total Revenue"
          value={`£${(stats.totalRevenue || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          icon={PoundSterling}
          color="green"
        />
        <StatCard
          title="Total Invoices"
          value={stats.totalInvoices || 0}
          icon={FileText}
          color="purple"
        />
        <StatCard
          title="Pending"
          value={stats.pendingInvoices || 0}
          icon={Clock}
          color="yellow"
        />
      </motion.div>

      {/* Subscription Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
      >
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription Business Overview</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-3xl font-bold text-blue-900">{subscriptionStats.totalActiveSubscriptions}</p>
            <p className="text-xs text-blue-700 mt-1">Total Active Subscriptions</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3">
            <p className="text-3xl font-bold text-green-900">£{(subscriptionStats.currentMonthRevenue || 0).toFixed(2)}</p>
            <p className="text-xs text-green-700 mt-1">Monthly Revenue</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-3">
            <p className="text-2xl font-bold text-purple-900">{subscriptionStats.paidSubscriptions}</p>
            <p className="text-xs text-purple-700 mt-1">Paid Subscriptions</p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-3">
            <p className="text-2xl font-bold text-yellow-900">{subscriptionStats.trialSubscriptions}</p>
            <p className="text-xs text-yellow-700 mt-1">Trial Subscriptions</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-3">
            <p className="text-2xl font-bold text-orange-900">{subscriptionStats.newSubscriptionsThisMonth}</p>
            <p className="text-xs text-orange-700 mt-1">New This Month</p>
          </div>
          <div className="bg-red-50 rounded-lg p-3">
            <p className="text-2xl font-bold text-red-900">{subscriptionStats.cancelledSubscriptions}</p>
            <p className="text-xs text-red-700 mt-1">Cancelled</p>
          </div>
        </div>
      </motion.div>

      {/* Recent Invoices */}
      {recentInvoices.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-3"
        >
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Recent Invoices</h2>
            <Link to="/staff/invoices" className="text-sm text-[#1F11ED] font-medium">
              View All
            </Link>
          </div>
          <div className="space-y-2">
            {recentInvoices.slice(0, 5).map((invoice) => (
              <div key={invoice.id} className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{invoice.invoiceNumber || invoice.invoice_number}</p>
                    <p className="text-sm text-gray-600">{invoice.sender?.companyName || invoice.senderName || 'N/A'}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">£{Number(invoice.total || 0).toFixed(2)}</p>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                      invoice.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                      invoice.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {invoice.status === 'paid' ? '💰' : invoice.status === 'approved' ? '✅' : invoice.status === 'rejected' ? '❌' : '⏳'} {invoice.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Recent Clients */}
      {recentClients.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="space-y-3"
        >
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Recent Clients</h2>
            <Link to="/staff/manage-clients" className="text-sm text-[#1F11ED] font-medium">
              View All
            </Link>
          </div>
          <div className="space-y-2">
            {recentClients.slice(0, 5).map((client) => (
              <div key={client.id} className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{client.firstName} {client.lastName}</p>
                    <p className="text-sm text-gray-600">{client.companyName || client.company_name || 'N/A'}</p>
                    <p className="text-xs text-gray-500">{client.email}</p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      client.subscriptionPlan === 'pro' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {client.subscriptionPlan === 'pro' ? '✅ Pro' : '🔧 Essential'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Recent Activity */}
      {recentActivity.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="space-y-3"
        >
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Recent Activities</h2>
            <Link to="/staff/security" className="text-sm text-[#1F11ED] font-medium">
              View All
            </Link>
          </div>
          
          <div className="space-y-2">
            {recentActivity.slice(0, 5).map((activity, index) => (
              <div key={activity.id || index} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      {activity.staffName || activity.staff_name || activity.user_name || activity.userName || 'Unknown User'} - {activity.details?.message || activity.action}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(activity.timestamp || activity.created_at).toLocaleString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default MobileStaffDashboard;
